import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Plus, FolderOpen, Calendar, Tag, ChevronRight, LayoutGrid, List, Trash2 } from 'lucide-react'
import Header from '../components/Header'
import { useCreateProject, useDeleteProject, useProjects } from "./hooks/useProjects";
import { useAuth } from "../authentication/AuthContext";
import AddProjectModal from "./components/AddProjectModal";
import Sidebar from "../components/Sidebar";

const PRIORITY_MAP = {
    1: { label: 'High',   color: 'bg-red-500/15 text-red-400 border-red-500/30' },
    2: { label: 'Medium', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
    3: { label: 'Low',    color: 'bg-green-500/15 text-green-400 border-green-500/30' },
};

const CATEGORY_COLOR = {
    WORK:      'bg-blue-500/15 text-blue-400',
    PERSONAL:  'bg-purple-500/15 text-purple-400',
    EDUCATION: 'bg-[var(--brand-coral)]/15 text-[var(--brand-coral)]',
    HEALTH:    'bg-teal-500/15 text-teal-400',
    FINANCE:   'bg-orange-500/15 text-orange-400',
    OTHER:     'bg-gray-500/15 text-[var(--text-muted)]',
};

function formatDate(ms) {
    if (!ms) return null;
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ProjectCard({ project, isSelected, onToggleSelection, isDeleting, isSelectionMode }) {
    const navigate = useNavigate();
    const priority = PRIORITY_MAP[project.priority];
    const categoryColor = CATEGORY_COLOR[project.category] || CATEGORY_COLOR.OTHER;
    const handleOpenProject = () => navigate(`/project/${project.id}`);

    return (
        <div className="relative">
            {isSelectionMode && <input type="checkbox" checked={isSelected} onChange={onToggleSelection} disabled={isDeleting} aria-label={`Select ${project.name || 'Untitled Project'}`} className="absolute left-4 top-5 z-10 h-4 w-4 cursor-pointer rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]" />}
            <button type="button" onClick={handleOpenProject} className={`group flex w-full cursor-pointer flex-col gap-3 rounded-2xl border bg-[var(--surface)] py-5 pr-5 text-left transition-all duration-200 hover:border-[var(--brand-coral)]/50 hover:shadow-lg hover:shadow-[var(--brand-coral)]/5 ${isSelectionMode ? 'pl-12' : 'pl-5'} ${isSelected ? 'border-[var(--brand-coral)]/60 ring-1 ring-[var(--brand-coral)]/30' : 'border-[var(--border-color)]'}`}>

            {/* Top row: category + priority */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                {project.category && (
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${categoryColor}`}>
                        <Tag size={10} />
                        {project.category.charAt(0) + project.category.slice(1).toLowerCase()}
                    </span>
                )}
                {priority && (
                    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border ${priority.color}`}>
                        {priority.label}
                    </span>
                )}
            </div>

            {/* Name */}
            <div className="flex items-start justify-between gap-2">
                <h3 className="text-[var(--text-primary)] font-semibold text-base leading-snug group-hover:text-[var(--brand-coral)] transition-colors line-clamp-2">
                    {project.name || 'Untitled Project'}
                </h3>
                <ChevronRight size={16} className="text-[var(--text-muted)] group-hover:text-[var(--brand-coral)] transition-colors mt-0.5 shrink-0" />
            </div>

            {/* Description */}
            {project.description && (
                <p className="text-[var(--text-muted)] text-sm line-clamp-2 leading-relaxed">
                    {project.description}
                </p>
            )}

            {/* Goal */}
            {project.goal && (
                <div className="bg-[var(--surface-hover)] rounded-lg px-3 py-2">
                    <p className="text-[var(--text-muted)] text-xs line-clamp-2">
                        <span className="text-[var(--text-muted)] font-medium mr-1">Goal:</span>
                        {project.goal}
                    </p>
                </div>
            )}

            {/* Dates */}
            {(project.start_date || project.end_date) && (
                <div className="flex items-center gap-3 mt-auto pt-2 border-t border-[var(--border-color)]">
                    <Calendar size={12} className="text-[var(--text-muted)] shrink-0" />
                    <span className="text-[var(--text-muted)] text-xs">
                        {formatDate(project.start_date)}
                        {project.start_date && project.end_date && <span className="mx-1">→</span>}
                        {formatDate(project.end_date)}
                    </span>
                </div>
            )}
            </button>
        </div>
    );
}

ProjectCard.propTypes = {
    project: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        goal: PropTypes.string,
        category: PropTypes.string,
        priority: PropTypes.number,
        start_date: PropTypes.number,
        end_date: PropTypes.number,
    }).isRequired,
};

function ProjectListRow({ project, isSelected, onToggleSelection, isDeleting, isSelectionMode }) {
    const navigate = useNavigate();
    const priority = PRIORITY_MAP[project.priority];
    const categoryColor = CATEGORY_COLOR[project.category] || CATEGORY_COLOR.OTHER;
    const handleOpenProject = () => navigate(`/project/${project.id}`);

    return (
        <div className="relative">
            {isSelectionMode && <input type="checkbox" checked={isSelected} onChange={onToggleSelection} disabled={isDeleting} aria-label={`Select ${project.name || 'Untitled Project'}`} className="absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 cursor-pointer rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]" />}
            <button type="button" onClick={handleOpenProject} className={`group flex w-full cursor-pointer items-center gap-4 rounded-xl border bg-[var(--surface)] py-3 pr-4 text-left transition-all duration-200 hover:border-[var(--brand-coral)]/50 hover:shadow-lg hover:shadow-[var(--brand-coral)]/5 ${isSelectionMode ? 'pl-12' : 'pl-4'} ${isSelected ? 'border-[var(--brand-coral)]/60 ring-1 ring-[var(--brand-coral)]/30' : 'border-[var(--border-color)]'}`}>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[var(--text-primary)] font-semibold text-sm group-hover:text-[var(--brand-coral)] transition-colors truncate">
                        {project.name || 'Untitled Project'}
                    </span>
                    {project.category && (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${categoryColor}`}>
                            <Tag size={9} />
                            {project.category.charAt(0) + project.category.slice(1).toLowerCase()}
                        </span>
                    )}
                    {priority && (
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${priority.color}`}>
                            {priority.label}
                        </span>
                    )}
                </div>
                {project.description && (
                    <p className="mt-0.5 text-[var(--text-muted)] text-xs truncate">{project.description}</p>
                )}
            </div>
            {(project.start_date || project.end_date) && (
                <div className="hidden sm:flex items-center gap-1.5 text-[var(--text-muted)] text-xs shrink-0">
                    <Calendar size={11} />
                    <span>
                        {formatDate(project.start_date)}
                        {project.start_date && project.end_date && <span className="mx-1">→</span>}
                        {formatDate(project.end_date)}
                    </span>
                </div>
            )}
            <ChevronRight size={15} className="text-[var(--text-muted)] group-hover:text-[var(--brand-coral)] transition-colors shrink-0" />
            </button>
        </div>
    );
}

ProjectListRow.propTypes = {
    project: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        category: PropTypes.string,
        priority: PropTypes.number,
        start_date: PropTypes.number,
        end_date: PropTypes.number,
    }).isRequired,
};

function ProjectsPage() {
    const [showModal, setShowModal] = useState(false)
    const [viewMode, setViewMode] = useState('grid')
    const [isSelectionMode, setIsSelectionMode] = useState(false)
    const [selectedProjectIds, setSelectedProjectIds] = useState(() => new Set())
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [pageSize, setPageSize] = useState(25)
    const [currentPage, setCurrentPage] = useState(1)
    const { authToken } = useAuth();
    const { data: projects, isLoading } = useProjects(authToken)
    const { mutate: createProject } = useCreateProject(authToken)
    const { mutateAsync: deleteProject, isLoading: isDeleting, error: deleteError, reset: resetDeleteError } = useDeleteProject(authToken)
    const projectList = projects ?? []
    const totalPages = Math.max(1, Math.ceil(projectList.length / pageSize))
    const safeCurrentPage = Math.min(currentPage, totalPages)
    const pageStart = (safeCurrentPage - 1) * pageSize
    const paginatedProjects = projectList.slice(pageStart, pageStart + pageSize)
    const paginatedIds = paginatedProjects.map(project => String(project.id))
    const allPaginatedSelected = paginatedIds.length > 0 && paginatedIds.every(id => selectedProjectIds.has(id))

    React.useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const handleSubmit = (data) => {
        createProject(data)
        setShowModal(false)
    }

    const toggleSelection = (id) => setSelectedProjectIds(current => {
        const next = new Set(current)
        const normalizedId = String(id)
        next.has(normalizedId) ? next.delete(normalizedId) : next.add(normalizedId)
        return next
    })

    const toggleCurrentPageSelection = () => setSelectedProjectIds(current => {
        const next = new Set(current)
        paginatedIds.forEach(id => allPaginatedSelected ? next.delete(id) : next.add(id))
        return next
    })

    const toggleSelectionMode = () => {
        if (isDeleting) return

        setIsSelectionMode(current => {
            if (current) setSelectedProjectIds(new Set())
            return !current
        })
    }

    const handleDeleteSelected = async () => {
        if (selectedProjectIds.size === 0 || isDeleting) return
        try {
            await Promise.all(Array.from(selectedProjectIds).map(id => deleteProject(id)))
            setSelectedProjectIds(new Set())
            setIsSelectionMode(false)
            setShowDeleteDialog(false)
        } catch {
            // Keep the selection and dialog open so the user can retry.
        }
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header />
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar />
                <div className="flex min-w-0 flex-1 flex-col rounded-[10px] bg-[var(--surface)]">
                    <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 py-8 md:px-6 md:py-10">

                        {/* Page header */}
                        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--text-primary)]">
                                    <FolderOpen size={24} className="text-[var(--brand-coral)]" />
                                    Projects
                                </h1>
                                <p className="mt-1 text-sm text-[var(--text-muted)]">
                                    {projects?.length ? `${projects.length} project(s)` : 'No projects yet'}
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                {isSelectionMode && (
                                    <button type="button" onClick={() => { resetDeleteError(); setShowDeleteDialog(true) }} disabled={selectedProjectIds.size === 0 || isDeleting} className="inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40">
                                        <Trash2 size={16} />
                                        {isDeleting ? 'Deleting...' : `Delete${selectedProjectIds.size ? ` (${selectedProjectIds.size})` : ''}`}
                                    </button>
                                )}
                                <button type="button" onClick={toggleSelectionMode} disabled={isDeleting} aria-pressed={isSelectionMode} className="inline-flex items-center rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-40">
                                    {isSelectionMode ? 'Cancel' : 'Select'}
                                </button>
                                <div className="flex items-center rounded-lg border border-[var(--border-color)] bg-[var(--surface)] p-1">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                                        title="Grid view"
                                    >
                                        <LayoutGrid size={15} />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`rounded-md p-1.5 transition-colors ${viewMode === 'list' ? 'bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                                        title="List view"
                                    >
                                        <List size={15} />
                                    </button>
                                </div>
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] shadow-lg transition hover:brightness-105 active:brightness-95"
                                >
                                    <Plus size={16} />
                                    New Project
                                </button>
                            </div>
                        </div>

                        {isSelectionMode && !isLoading && projectList.length > 0 && (
                            <div className="mb-3 flex items-center justify-between rounded-lg border border-[var(--border-color)] bg-[var(--surface)] px-4 py-2">
                                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--text-muted)]"><input type="checkbox" checked={allPaginatedSelected} onChange={toggleCurrentPageSelection} disabled={isDeleting} className="h-4 w-4 rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]" />Select this page</label>
                                {selectedProjectIds.size > 0 && <span className="text-xs text-[var(--brand-coral)]">{selectedProjectIds.size} selected</span>}
                            </div>
                        )}

                        {/* Content */}
                        {isLoading && (
                            <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-2'}>
                                {new Array(6).fill(null).map((_, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={`skeleton-${i}`} className={`rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] animate-pulse ${viewMode === 'grid' ? 'h-40 p-5' : 'h-14 px-4'}`} />
                                ))}
                            </div>
                        )}
                        {!isLoading && projects && projects.length > 0 && viewMode === 'grid' && (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {paginatedProjects.map((project) => (
                                    <ProjectCard key={project.id} project={project} isSelected={selectedProjectIds.has(String(project.id))} onToggleSelection={() => toggleSelection(project.id)} isDeleting={isDeleting} isSelectionMode={isSelectionMode} />
                                ))}
                            </div>
                        )}
                        {!isLoading && projects && projects.length > 0 && viewMode === 'list' && (
                            <div className="flex flex-col gap-2">
                                {paginatedProjects.map((project) => (
                                    <ProjectListRow key={project.id} project={project} isSelected={selectedProjectIds.has(String(project.id))} onToggleSelection={() => toggleSelection(project.id)} isDeleting={isDeleting} isSelectionMode={isSelectionMode} />
                                ))}
                            </div>
                        )}
                        {!isLoading && projectList.length > 0 && (
                            <div className="mt-auto flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                                    <span>Rows per page</span>
                                    <select
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value))
                                            setCurrentPage(1)
                                        }}
                                        className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                        aria-label="Projects per page"
                                    >
                                        {[25, 50, 100].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                    <span>
                                        {pageStart + 1}–{Math.min(pageStart + pageSize, projectList.length)} of {projectList.length}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="min-w-20 text-center text-xs text-[var(--text-muted)]">
                                        Page {safeCurrentPage} of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                        {!isLoading && (!projects || projects.length === 0) && (
                            <div className="flex flex-col items-center justify-center gap-4 py-28">
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--border-color)] bg-[var(--surface)]">
                                    <FolderOpen size={28} className="text-[var(--text-muted)]" />
                                </div>
                                <div className="text-center">
                                    <h2 className="text-lg font-semibold text-[var(--text-secondary)]">No projects yet</h2>
                                    <p className="mt-1 text-sm text-[var(--text-muted)]">Create your first project to get started.</p>
                                </div>
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-4 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] transition hover:brightness-105"
                                >
                                    <Plus size={16} />
                                    New Project
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showDeleteDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !isDeleting) setShowDeleteDialog(false) }}>
                    <div role="dialog" aria-modal="true" aria-labelledby="delete-projects-title" className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-6 shadow-2xl shadow-black/50">
                        <div className="flex items-start gap-4"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 text-red-300"><Trash2 size={20} /></div><div><h2 id="delete-projects-title" className="text-lg font-semibold text-[var(--text-primary)]">Delete selected projects?</h2><p className="mt-2 text-sm leading-6 text-[var(--text-muted)]">{selectedProjectIds.size} selected project{selectedProjectIds.size === 1 ? '' : 's'} will be permanently deleted. This action cannot be undone.</p></div></div>
                        {deleteError && <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">Some selected projects could not be deleted. Please try again.</p>}
                        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting} className="rounded-lg border border-[var(--border-strong)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:opacity-50">Cancel</button><button type="button" onClick={handleDeleteSelected} disabled={isDeleting || selectedProjectIds.size === 0} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:opacity-50"><Trash2 size={16} />{isDeleting ? 'Deleting...' : 'Delete permanently'}</button></div>
                    </div>
                </div>
            )}

            {showModal && <AddProjectModal onSubmit={handleSubmit} onClose={() => setShowModal(false)} />}
        </div>
    )
}

export default ProjectsPage
